
// Removed date-fns-tz imports - now using simple local time approach

/**
 * Ensures a time string is in the format HH:mm:ss
 * @param time Time string in HH:mm or HH:mm:ss format
 * @returns Formatted time string in HH:mm:ss format
 */
export const normalizeTimeFormat = (time: string): string => {
  if (!time) return '';
  
  // If time is already in HH:mm:ss format, return as is
  if (time.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return time;
  }
  
  // If time is in HH:mm format, append :00
  if (time.match(/^\d{2}:\d{2}$/)) {
    return `${time}:00`;
  }
  
  return time;
};

/**
 * Generates time slots between start and end hours
 * @param startHour Starting hour (default: 7)
 * @param endHour Ending hour (default: 19)
 * @param interval Minutes between slots (default: 30)
 * @returns Array of time slots in HH:mm format
 */
export const generateTimeSlots = (
  startHour: number = 7,
  endHour: number = 19,
  interval: 30 | 60 = 30
): string[] => {
  const slots: string[] = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    const formattedHour = hour.toString().padStart(2, '0');
    
    // Add the hour slot (XX:00)
    slots.push(`${formattedHour}:00`);
    
    // Add half-hour slot (XX:30) if interval is 30
    if (interval === 30 && hour < endHour) {
      slots.push(`${formattedHour}:30`);
    }
  }
  
  return slots;
};

/**
 * Gets available end times based on a start time
 * @param startTime Selected start time
 * @param timeSlots Available time slots
 * @returns Array of time slots that come after the start time
 */
export const getAvailableEndTimes = (
  startTime: string,
  timeSlots: string[]
): string[] => {
  if (!startTime) return [];
  
  const startIndex = timeSlots.indexOf(startTime);
  if (startIndex === -1) return [];
  
  return timeSlots.slice(startIndex + 1);
};

/**
 * Converts slot size string to duration in hours
 * @param slotSize Slot size ("15", "30", "60", "90", "120", "allDay", etc.)
 * @returns Duration in hours
 */
export const slotSizeToDurationHours = (slotSize: string): number => {
  switch (slotSize) {
    case '15':
      return 0.25;
    case '30':
      return 0.5;
    case '60':
      return 1;
    case '90':
      return 1.5;
    case '120':
      return 2;
    case '150':
      return 2.5;
    case '180':
      return 3;
    case '240':
      return 4;
    case 'allDay':
      return 12; // 12 hours for all day
    default:
      // If it's a number, convert minutes to hours
      if (!isNaN(Number(slotSize))) {
        return Number(slotSize) / 60;
      }
      return 1; // Default to 1 hour
  }
};

/**
 * Calculates default end time based on start time
 * @param startTime Selected start time
 * @param durationHours Duration in hours (default: 1)
 * @returns End time in HH:mm format
 */
export const getDefaultEndTime = (
  startTime: string,
  durationHours: number = 1
): string => {
  if (!startTime) return '';
  
  const [hours, minutes] = startTime.split(':').map(Number);
  let endHours = hours + Math.floor(durationHours);
  let endMinutes = minutes + (durationHours % 1) * 60;
  
  if (endMinutes >= 60) {
    endHours += 1;
    endMinutes -= 60;
  }
  
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

/**
 * Converts 24-hour format time to 12-hour format
 * @param time Time string in 24-hour format (HH:mm)
 * @returns Time string in 12-hour format with AM/PM
 */
export const convertTo12HourFormat = (time: string): string => {
  if (!time) return '';
  
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  
  if (isNaN(hour)) return time;
  
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  
  return `${displayHour}:${minutes} ${period}`;
};

/**
 * Formats time from Date object to HH:mm format
 * @param date Date object
 * @returns Time string in HH:mm format
 */
export const formatTimeFromDate = (date: Date): string => {
  if (!date || !(date instanceof Date)) return '';
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Calculates the optimal display time range based on operating hours
 * @param operatingHours Array of operating hours by day of week
 * @param defaultRange Default time range from admin settings
 * @returns Object with minTime and maxTime in HH:mm:ss format
 */
export const calculateOptimalTimeRange = (
  operatingHours: any[],
  defaultRange: { minTime: string; maxTime: string }
): { minTime: string; maxTime: string } => {
  // Start with default values
  let minTime = defaultRange.minTime;
  let maxTime = defaultRange.maxTime;
  
  // Check all operating hours to find the earliest start and latest end
  operatingHours.forEach(hours => {
    if (hours.startTime && hours.startTime + ':00' < minTime) {
      minTime = hours.startTime + ':00';
    }
    if (hours.endTime && hours.endTime + ':00' > maxTime) {
      maxTime = hours.endTime + ':00';
    }
  });
  
  return { minTime, maxTime };
};

/**
 * Creates local date and time strings for database storage (simplified)
 * @param date Date object for the day
 * @param time Time string in HH:mm format  
 * @returns Object with separate date and time strings for database storage
 */
export const createLocalDateTime = (date: Date, time: string): { date: string, time: string } => {
  // Use local date methods to avoid timezone conversion
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD format
  const timeStr = normalizeTimeFormat(time); // HH:mm:ss format
  
  // console.log(`📅 Simple storage: ${dateStr} ${timeStr} (local time)`);
  
  return { date: dateStr, time: timeStr };
};

/**
 * Creates a Date object from stored date and time (backward compatible)
 * @param dateTimeOrDate Either a full datetime string (old format) or date string (new format) 
 * @param timeStr Optional time string for new format
 * @returns Local Date object
 */
export const parseStoredDateTime = (dateTimeOrDate: string, timeStr?: string): Date => {
  if (!dateTimeOrDate) return new Date();
  
  // New format: separate date and time strings
  if (timeStr) {
    const [year, month, day] = dateTimeOrDate.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    const localDate = new Date(year, month - 1, day, hours, minutes, 0);
    // console.log(`🔄 Simple parsing (new): ${dateTimeOrDate} ${timeStr} -> ${localDate.toLocaleString()}`);
    return localDate;
  }
  
  // Legacy format: full datetime string (backward compatibility)
  // Handle formats like "2024-01-15T14:30:00" or "2024-01-15 14:30:00"
  const normalizedDateTime = dateTimeOrDate.replace('T', ' ').replace('Z', '').split('.')[0];
  const [datePart, timePart] = normalizedDateTime.split(' ');
  
  if (datePart && timePart) {
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    const localDate = new Date(year, month - 1, day, hours, minutes, 0);
    // console.log(`🔄 Simple parsing (legacy): ${dateTimeOrDate} -> ${localDate.toLocaleString()}`);
    return localDate;
  }
  
  // Fallback to direct parsing
  return new Date(dateTimeOrDate);
};

/**
 * Formats a Date object for FullCalendar without timezone conversion
 * @param date Date object in local time
 * @returns Local datetime string in format "YYYY-MM-DDTHH:mm:ss"
 */
export const formatDateForCalendar = (date: Date): string => {
  if (!date || !(date instanceof Date)) return '';
  
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Legacy function for backward compatibility - now simplified
 * @param date Date object for the day  
 * @param time Time string in HH:mm format
 * @returns Date string for database (keeping old interface)
 */
export const formatDateTimeForStorage = (date: Date, time: string): string => {
  const { date: dateStr, time: timeStr } = createLocalDateTime(date, time);
  return `${dateStr} ${timeStr}`;
};
